import { CurrencyPipe, DatePipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { addDays, format, parseISO, startOfToday } from 'date-fns';
import { z } from 'zod';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';
import { PayeeIconButtonComponent } from '../../payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeDatepickerComponent } from '../../payee-form-fields/payee-datepicker/payee-datepicker.component';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeDropzoneComponent } from '../../payee-dropzone/payee-dropzone.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { PayeeInputDirective } from '../../payee-input/payee-input.directive';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import { PayeeWorkflowProvidedMediaComponent } from '../../payee-workflow-provided-media/payee-workflow-provided-media.component';
import {
  RunTransition,
  RunTransitionWithDialog,
  WorkflowService,
} from '../../services/workflow/workflow.service';
import { requiredFileListUnlessProvidedMedia } from '../workflow-file-upload-validators';

const claimType = z.enum([
  'principal',
  'interest',
  'flat_rate_cost',
  'procedural_fee',
  'payee_fee',
]);

const claimStatement = z.object({
  id: z.string(),
  claim_type: claimType,
  principal_index: z.number().int().min(0).nullable(),
  mokk_claim_id: z.string().nullable(),
  related_claim_id: z.string().nullable(),
  interest_type: z.string().nullable(),
  interest_start_date: z.string().date().nullable(),
  due_date: z.string().date().nullable(),
  label: z.string(),
  currency: z.string().length(3),
  total: z.number().int().min(0),
  acknowledged: z.number().int().min(0),
  disputed: z.number().int().min(0),
  paid_before: z.number().int().min(0),
  paid_after: z.number().int().min(0),
});
type ClaimStatement = z.infer<typeof claimStatement>;

const principalClaim = claimStatement.extend({
  optional_claims: z.array(claimStatement),
  available_optional_claims: z.array(claimStatement),
});
type PrincipalClaim = z.infer<typeof principalClaim>;

const dialogDataSchema = z.object({
  issued_at: z.string().date().nullable(),
  principal_claims: z.array(principalClaim),
  available_principal_claims: z.array(principalClaim),
  fee_claims: z.array(claimStatement),
  available_fee_claims: z.array(claimStatement),
});
type DialogData = z.infer<typeof dialogDataSchema>;

type AmountForm = {
  id: FormControl<string>;
  acknowledged: FormControl<string>;
  disputed: FormControl<string>;
  paid_before: FormControl<string>;
  paid_after: FormControl<string>;
};

type PrincipalForm = AmountForm & {
  optional_claims: FormArray<FormGroup<AmountForm>>;
};

@Component({
  selector: 'app-debtor-statement-dialog',
  templateUrl: './debtor-statement-dialog.component.html',
  styleUrls: ['./debtor-statement-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    PayeeButtonComponent,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    PayeeFormFieldComponent,
    PayeeDatepickerComponent,
    PayeeDropzoneComponent,
    PayeeWorkflowProvidedMediaComponent,
    PayeeInputDirective,
    MatDialogClose,
    ReactiveFormsModule,
    TranslateModule,
    CurrencyPipe,
    DatePipe,
    DecimalPipe,
    NgTemplateOutlet,
  ],
})
export class DebtorStatementDialogComponent implements OnInit {
  private readonly dialogRef = inject<MatDialogRef<
    DebtorStatementDialogComponent,
    RunTransition | undefined
  >>(MatDialogRef);
  readonly data = inject<RunTransitionWithDialog>(MAT_DIALOG_DATA);
  private readonly workflowService = inject(WorkflowService);
  private readonly fb = inject(FormBuilder);

  readonly loading = signal(false);
  readonly claimsMissing = signal(false);
  readonly dialogData = signal<DialogData | null>(null);
  readonly minDeadline = addDays(startOfToday(), 1);

  readonly form = this.fb.group({
    hard_deadline_at: this.fb.control<Date | null>(null, Validators.required),
    principal_claims: this.fb.array<FormGroup<PrincipalForm>>([]),
    fee_claims: this.fb.array<FormGroup<AmountForm>>([]),
    files: this.fb.control<File[]>([], {
      validators: [
        ...requiredFileListUnlessProvidedMedia(this.data.media),
        Validators.maxLength(10),
      ],
      nonNullable: true,
    }),
  });

  async ngOnInit(): Promise<void> {
    try {
      this.loading.set(true);
      const data = dialogDataSchema.parse(await this.workflowService.getDialogData({
        caseId: this.data.caseId,
        transitionId: this.data.transitionId,
        params: this.data.extraParams,
      }));

      if (data.issued_at) {
        this.form.controls.hard_deadline_at.setValue(addDays(parseISO(data.issued_at), 15));
      }

      for (const claim of data.principal_claims) {
        this.form.controls.principal_claims.push(this.createPrincipalForm(claim));
      }
      for (const claim of data.fee_claims) {
        this.form.controls.fee_claims.push(this.createAmountForm(claim));
      }

      this.dialogData.set(data);
    } catch (error) {
      console.error('Error while loading dialog data', error);
      this.workflowService.showTransitionError(error);
      this.dialogRef.close();
    } finally {
      this.loading.set(false);
    }
  }

  principalRow(principalId: string | undefined): PrincipalClaim | undefined {
    if (!principalId) {
      return undefined;
    }

    const data = this.dialogData();
    return data?.principal_claims.find((claim) => claim.id === principalId)
      ?? data?.available_principal_claims.find((claim) => claim.id === principalId);
  }

  feeRow(claimId: string | undefined): ClaimStatement | undefined {
    if (!claimId) {
      return undefined;
    }

    const data = this.dialogData();
    return data?.fee_claims.find((claim) => claim.id === claimId)
      ?? data?.available_fee_claims.find((claim) => claim.id === claimId);
  }

  availablePrincipals(): PrincipalClaim[] {
    const data = this.dialogData();
    const used = this.usedIds(this.form.controls.principal_claims.controls.map((claimForm) => claimForm.controls.id.value));

    return [...(data?.principal_claims ?? []), ...(data?.available_principal_claims ?? [])]
      .filter((claim) => !used.has(claim.id));
  }

  availableFees(): ClaimStatement[] {
    const data = this.dialogData();
    const used = this.usedIds(this.form.controls.fee_claims.controls.map((claimForm) => claimForm.controls.id.value));

    return [...(data?.fee_claims ?? []), ...(data?.available_fee_claims ?? [])]
      .filter((claim) => !used.has(claim.id));
  }

  availableOptional(principalId: string): ClaimStatement[] {
    const principal = this.principalRow(principalId);
    return principal ? this.unusedAncillaries(principal) : [];
  }

  ancillaries(principal: PrincipalClaim): ClaimStatement[] {
    return this.unusedAncillaries(principal);
  }

  optionalClaim(principalId: string, claimId: string | undefined): ClaimStatement | undefined {
    const principal = this.principalRow(principalId);
    if (!principal || !claimId) {
      return undefined;
    }

    return principal.optional_claims.find((claim) => claim.id === claimId)
      ?? principal.available_optional_claims.find((claim) => claim.id === claimId);
  }

  addPrincipal(claim: PrincipalClaim): void {
    if (this.form.controls.principal_claims.controls.some((claimForm) => claimForm.controls.id.value === claim.id)) {
      return;
    }

    this.form.controls.principal_claims.push(this.createPrincipalForm(claim));
    this.claimsMissing.set(false);
  }

  addFee(claim: ClaimStatement): void {
    if (this.form.controls.fee_claims.controls.some((claimForm) => claimForm.controls.id.value === claim.id)) {
      return;
    }

    this.form.controls.fee_claims.push(this.createAmountForm(claim));
    this.claimsMissing.set(false);
  }

  addOptional(principalForm: FormGroup<PrincipalForm>, claim: ClaimStatement): void {
    if (principalForm.controls.optional_claims.controls.some((claimForm) => claimForm.controls.id.value === claim.id)) {
      return;
    }

    principalForm.controls.optional_claims.push(this.createAmountForm(claim));
    this.claimsMissing.set(false);
  }

  addAncillary(principal: PrincipalClaim, claim: ClaimStatement): void {
    this.addPrincipal(principal);
    const principalForm = this.form.controls.principal_claims.controls.find(
      (claimForm) => claimForm.controls.id.value === principal.id,
    );
    if (principalForm) {
      this.addOptional(principalForm, claim);
    }
  }

  removePrincipal(index: number): void {
    this.form.controls.principal_claims.removeAt(index);
  }

  removeFee(index: number): void {
    this.form.controls.fee_claims.removeAt(index);
  }

  removeOptional(principalForm: FormGroup<PrincipalForm>, index: number): void {
    principalForm.controls.optional_claims.removeAt(index);
  }

  onDrop(files: FileList): void {
    const fileValues = [...this.form.controls.files.value];
    for (let i = 0; i < files.length && fileValues.length < 10; ++i) {
      const file = files.item(i);
      if (file) {
        fileValues.push(file);
      }
    }
    this.form.controls.files.setValue(fileValues);
    this.form.controls.files.markAsDirty();
  }

  removeFile(index: number): void {
    const fileValues = [...this.form.controls.files.value];
    fileValues.splice(index, 1);
    this.form.controls.files.setValue(fileValues);
    this.form.controls.files.markAsDirty();
  }

  async submit(): Promise<void> {
    this.form.markAllAsTouched();
    const hasClaims = this.form.controls.principal_claims.length > 0
      || this.form.controls.fee_claims.length > 0;
    this.claimsMissing.set(!hasClaims);
    if (this.form.invalid || !this.dialogData() || !hasClaims) {
      return;
    }

    const deadline = this.form.controls.hard_deadline_at.value;
    const uploadedFiles = this.form.controls.files.value;
    const payload: RunTransition = {
      caseId: this.data.caseId,
      transitionId: this.data.transitionId,
      params: {
        ...(this.data.extraParams ?? {}),
        files: [...(this.data.media ?? []).map((media) => media.uuid), ...uploadedFiles],
        disputed_claims: this.claimPayload(),
        ...(deadline ? { hard_deadline_at: format(deadline, 'yyyy-MM-dd') } : {}),
      },
    };

    this.loading.set(true);
    try {
      await this.workflowService.runTransition(payload);
      this.dialogRef.close(payload);
    } catch (error) {
      this.workflowService.showTransitionError(error);
    } finally {
      this.loading.set(false);
    }
  }

  private createPrincipalForm(claim: PrincipalClaim): FormGroup<PrincipalForm> {
    return this.fb.group<PrincipalForm>({
      ...this.amountControls(claim),
      optional_claims: this.fb.array<FormGroup<AmountForm>>([]),
    }, {
      validators: [this.amountsSumValidator(claim.total)],
    });
  }

  private unusedAncillaries(principal: PrincipalClaim): ClaimStatement[] {
    const form = this.form.controls.principal_claims.controls.find(
      (claimForm) => claimForm.controls.id.value === principal.id,
    );
    const used = this.usedIds(form?.controls.optional_claims.controls.map((claimForm) => claimForm.controls.id.value) ?? []);

    return [...principal.optional_claims, ...principal.available_optional_claims]
      .filter((claim) => !used.has(claim.id));
  }

  private createAmountForm(claim: ClaimStatement): FormGroup<AmountForm> {
    return this.fb.group(this.amountControls(claim), {
      validators: [this.amountsSumValidator(claim.total)],
    });
  }

  private amountControls(claim: ClaimStatement): AmountForm {
    return {
      id: this.fb.control(claim.id, { nonNullable: true }),
      acknowledged: this.createAmountControl(claim.acknowledged),
      disputed: this.createAmountControl(claim.disputed),
      paid_before: this.createAmountControl(claim.paid_before),
      paid_after: this.createAmountControl(claim.paid_after),
    };
  }

  private createAmountControl(amount: number): FormControl<string> {
    return this.fb.control(this.formatAmount(amount), {
      nonNullable: true,
      validators: [this.formatAmountValidator],
    });
  }

  private amountsSumValidator(total: number): ValidatorFn {
    return (control) => {
      const group = control as FormGroup<AmountForm>;
      const sum = this.amountValue(group.controls.acknowledged?.value)
        + this.amountValue(group.controls.disputed?.value)
        + this.amountValue(group.controls.paid_before?.value)
        + this.amountValue(group.controls.paid_after?.value);

      if (sum === total) {
        return null;
      }

      return { sum: { total, actual: sum } };
    };
  }

  private readonly formatAmountValidator: ValidatorFn = (control) => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      setTimeout(() => control.patchValue(this.formatAmount(0)));
      return null;
    }

    if (typeof value !== 'number' && typeof value !== 'string') {
      return { pattern: true };
    }

    const formattedValue = this.formatAmount(this.amountValue(value));
    if (formattedValue !== value) {
      setTimeout(() => control.patchValue(formattedValue));
    }

    return null;
  };

  private claimPayload(): Array<{
    claimType: ClaimStatement['claim_type'];
    claimId?: string;
    relatedClaimId?: string;
    currency: string;
    totalClaimAmount: number;
    acknowledgedAmount: number;
    disputedAmount: number;
    paidBeforeReceipt: number;
    paidAfterReceipt: number;
    dueDate?: string;
    interestStartDate?: string;
    interestType?: string;
  }> {
    const rows = [];
    for (const principal of this.form.controls.principal_claims.getRawValue()) {
      const principalClaim = this.principalRow(principal.id);
      if (principalClaim) {
        rows.push(this.amountPayload(principal, principalClaim));
      }
      for (const optional of principal.optional_claims) {
        const optionalClaim = this.optionalClaim(principal.id ?? '', optional.id);
        if (optionalClaim) {
          rows.push(this.amountPayload(optional, optionalClaim));
        }
      }
    }
    for (const fee of this.form.controls.fee_claims.getRawValue()) {
      const feeClaim = this.feeRow(fee.id);
      if (feeClaim) {
        rows.push(this.amountPayload(fee, feeClaim));
      }
    }

    return rows;
  }

  private amountPayload(row: {
    acknowledged?: string | null;
    disputed?: string | null;
    paid_before?: string | null;
    paid_after?: string | null;
  }, claim: ClaimStatement): {
    claimType: ClaimStatement['claim_type'];
    claimId?: string;
    relatedClaimId?: string;
    currency: string;
    totalClaimAmount: number;
    acknowledgedAmount: number;
    disputedAmount: number;
    paidBeforeReceipt: number;
    paidAfterReceipt: number;
    dueDate?: string;
    interestStartDate?: string;
    interestType?: string;
  } {
    const ids = this.claimIds(claim);

    return {
      claimType: claim.claim_type,
      ...ids,
      currency: claim.currency,
      totalClaimAmount: claim.total,
      acknowledgedAmount: this.amountValue(row.acknowledged),
      disputedAmount: this.amountValue(row.disputed),
      paidBeforeReceipt: this.amountValue(row.paid_before),
      paidAfterReceipt: this.amountValue(row.paid_after),
      ...(claim.due_date ? { dueDate: claim.due_date } : {}),
      ...(claim.interest_start_date ? { interestStartDate: claim.interest_start_date } : {}),
      ...(claim.interest_type ? { interestType: claim.interest_type } : {}),
    };
  }

  private claimIds(claim: ClaimStatement): { claimId?: string; relatedClaimId?: string } {
    if (claim.mokk_claim_id) {
      return {
        claimId: claim.mokk_claim_id,
        ...(claim.related_claim_id ? { relatedClaimId: claim.related_claim_id } : {}),
      };
    }

    if (claim.principal_index === null) {
      return {};
    }

    const suffix = `1${String(claim.principal_index + 1).padStart(2, '0')}`;
    const principalId = `FKA/${suffix}`;
    if (claim.claim_type === 'interest' || claim.claim_type === 'flat_rate_cost') {
      const prefix = claim.claim_type === 'interest' ? 'KMT' : 'EKT';

      return {
        claimId: `${prefix}/${suffix}`,
        relatedClaimId: principalId,
      };
    }

    return { claimId: principalId };
  }

  private usedIds(ids: Array<string | null | undefined>): Set<string> {
    return new Set(ids.filter((id): id is string => !!id));
  }

  private formatAmount(value: number): string {
    return value.toLocaleString('hu-HU').replace(/,/g, ' ');
  }

  private amountValue(value: string | number | null | undefined): number {
    if (typeof value === 'number') {
      return value;
    }

    return parseInt(String(value ?? '').replace(/\D/g, '') || '0', 10);
  }
}
